import { createSlice } from '@reduxjs/toolkit';

// Constants for better maintainability
const QUICK_FILTERS = {
  TODAY: 'today',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
  ALL_TIME: 'all_time'
};

const DEFAULT_PAGINATION = {
  limit: 100,
  offset: 0,
  total: 0,
  hasMore: false,
  currentPage: 1,
  totalPages: 1
};

const initialState = {
  filters: {
    quickFilter: QUICK_FILTERS.TODAY,
    dateRange: {
      start: null,
      end: null,
    },
    serviceCode: '',
    platform: '',
    resultFilter: '',
  },
  stats: {
    totalPlay: 0,
    totalWin: 0,
    customers: 0,
    newCustomers: 0,
    existingCustomers: 0,
    revenue: 0,
    grossRevenue: 0,
    netRevenue: 0,
    jackpotContribution: 0,
    totalGames: 0,
    totalBets: 0,
    avgBetAmount: 0,
    avgWinAmount: 0,
    winPercentage: 0,
  },
  chartData: [],
  leaderboards: {
    topWinners: [],
    topByPoints: [],
    topPlayers: [],
    topByGames: [],
  },
  revenueBreakdown: [],
  gameStats: [],
  customerStats: [],
  customers: {
    summary: {
      totalCustomers: 0,
      totalPlayAmount: 0,
      totalWinnings: 0,
      avgCustomerValue: 0,
      groupedBy: 'phone',
    },
    customers: [],
  },
  platformStats: [],
  winLossAnalysis: [],
  recentBets: {
    data: [],
    pagination: { ...DEFAULT_PAGINATION },
    filters: {}
  },
  serviceCodes: [],
  loading: false,
  error: null,
  loadingStates: {
    metrics: false,
    chart: false,
    leaderboards: false,
    revenueBreakdown: false,
    gameStats: false,
    customerStats: false,
    customers: false,
    platformStats: false,
    winLossAnalysis: false,
    recentBets: false,
    serviceCodes: false,
  },
  errorStates: {
    metrics: null,
    chart: null,
    leaderboards: null,
    revenueBreakdown: null,
    gameStats: null,
    customerStats: null,
    customers: null,
    platformStats: null,
    winLossAnalysis: null,
    recentBets: null,
    serviceCodes: null,
  },
  lastUpdated: {
    metrics: null,
    chart: null,
    leaderboards: null,
    revenueBreakdown: null,
    gameStats: null,
    customerStats: null,
    customers: null,
    platformStats: null,
    winLossAnalysis: null,
    recentBets: null,
    serviceCodes: null,
  },
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    updateFilters: (state, action) => {
      const newFilters = action.payload;
      if (newFilters.quickFilter && !Object.values(QUICK_FILTERS).includes(newFilters.quickFilter)) {
        console.warn(`Invalid quick filter: ${newFilters.quickFilter}`);
        newFilters.quickFilter = QUICK_FILTERS.TODAY;
      }
      if (newFilters.dateRange) {
        const { start, end } = newFilters.dateRange;
        if (start && end) {
          const startDate = new Date(start);
          const endDate = new Date(end);
          if (isNaN(startDate.getTime()) || isNaN(endDate.getTime()) || startDate > endDate) {
            console.warn('Invalid date range provided');
            newFilters.dateRange = { start: null, end: null };
          }
        }
      }
      state.filters = { ...state.filters, ...newFilters };
    },

    setQuickFilter: (state, action) => {
      const filter = action.payload;
      // Check if the filter is a valid quick filter option or an empty string (for clearing)
      if (Object.values(QUICK_FILTERS).includes(filter) || filter === '') {
        state.filters.quickFilter = filter;
        // If a *valid, non-empty* quick filter is being set, then clear the date range.
        // If the quick filter is being cleared (i.e., filter is ''), do not modify the date range.
        if (filter !== '' && Object.values(QUICK_FILTERS).includes(filter)) {
          state.filters.dateRange = { start: null, end: null };
        }
      } else {
        console.warn(`Invalid quick filter attempted: ${filter}.`);
        // Optionally, set a default quick filter if an invalid one is somehow passed
        // state.filters.quickFilter = QUICK_FILTERS.TODAY;
        // state.filters.dateRange = { start: null, end: null };
      }
    },

    setDateRange: (state, action) => {
      const { start, end } = action.payload;

      const newStart = start ? new Date(start) : null;
      const newEnd = end ? new Date(end) : null;

      // Validate date objects
      if ((start && isNaN(newStart.getTime())) || (end && isNaN(newEnd.getTime()))) {
          console.warn('Invalid date object or string provided to setDateRange');
          // Optionally reset to a safe state or do nothing
          // state.filters.dateRange = { start: null, end: null };
          return;
      }

      // Validate date range logic (start before end)
      if (newStart && newEnd && newStart > newEnd) {
          console.warn('Start date cannot be after end date');
          // Optionally reset to a safe state or do nothing
          // state.filters.dateRange = { start: state.filters.dateRange.start, end: state.filters.dateRange.start }; // or nullify
          return;
      }

      state.filters.dateRange = {
        start: newStart ? newStart.toISOString() : null,
        end: newEnd ? newEnd.toISOString() : null
      };

      // Clear quick filter only when a complete custom date range is selected
      if (newStart && newEnd) {
        state.filters.quickFilter = '';
      }
    },

    setServiceCode: (state, action) => {
      const serviceCode = action.payload;
      state.filters.serviceCode = typeof serviceCode === 'string' ? serviceCode.trim() : '';
    },

    setPlatform: (state, action) => {
      const platform = action.payload;
      state.filters.platform = typeof platform === 'string' ? platform.trim() : '';
    },

    setResultFilter: (state, action) => {
      const resultFilter = action.payload;
      state.filters.resultFilter = typeof resultFilter === 'string' ? resultFilter.trim() : '';
    },

    resetFilters: (state) => {
      state.filters = { ...initialState.filters };
    },

    setStats: (state, action) => {
      const stats = action.payload;
      if (stats && typeof stats === 'object') {
        state.stats = {
          totalPlay: Number(stats.totalPlay) || 0,
          totalWin: Number(stats.totalWin) || 0,
          customers: Number(stats.customers) || 0,
          newCustomers: Number(stats.newCustomers) || 0,
          existingCustomers: Number(stats.existingCustomers) || 0,
          revenue: Number(stats.revenue) || 0,
          grossRevenue: Number(stats.grossRevenue) || 0,
          netRevenue: Number(stats.netRevenue) || 0,
          jackpotContribution: Number(stats.jackpotContribution) || 0,
          totalGames: Number(stats.totalGames) || 0,
          totalBets: Number(stats.totalBets) || 0,
          avgBetAmount: Number(stats.avgBetAmount) || 0,
          avgWinAmount: Number(stats.avgWinAmount) || 0,
          winPercentage: Number(stats.winPercentage) || 0,
        };
        state.lastUpdated.metrics = new Date().toISOString();
        state.errorStates.metrics = null;
      } else {
        console.warn('Invalid stats data provided:', stats);
      }
    },

    setChartData: (state, action) => {
      const chartData = action.payload;
      if (Array.isArray(chartData)) {
        state.chartData = chartData;
        state.lastUpdated.chart = new Date().toISOString();
        state.errorStates.chart = null;
      } else {
        console.warn('Chart data must be an array:', chartData);
        state.chartData = [];
      }
    },

    setLeaderboards: (state, action) => {
      const leaderboards = action.payload;
      if (leaderboards && typeof leaderboards === 'object') {
        const validatedLeaderboards = {};
        const leaderboardTypes = ['topWinners', 'topByPoints', 'topPlayers', 'topByGames'];
        leaderboardTypes.forEach(type => {
          if (Array.isArray(leaderboards[type])) {
            validatedLeaderboards[type] = leaderboards[type];
          } else {
            console.warn(`Invalid leaderboard data for ${type}:`, leaderboards[type]);
            validatedLeaderboards[type] = [];
          }
        });
        state.leaderboards = { ...state.leaderboards, ...validatedLeaderboards };
        state.lastUpdated.leaderboards = new Date().toISOString();
        state.errorStates.leaderboards = null;
      } else {
        console.warn('Invalid leaderboards data provided:', leaderboards);
      }
    },

    setRevenueBreakdown: (state, action) => {
      const data = action.payload;
      if (Array.isArray(data)) {
        state.revenueBreakdown = data;
        state.lastUpdated.revenueBreakdown = new Date().toISOString();
        state.errorStates.revenueBreakdown = null;
      } else {
        console.warn('Revenue breakdown data must be an array:', data);
        state.revenueBreakdown = [];
      }
    },

    setGameStats: (state, action) => {
      const data = action.payload;
      if (Array.isArray(data)) {
        state.gameStats = data;
        state.lastUpdated.gameStats = new Date().toISOString();
        state.errorStates.gameStats = null;
      } else {
        console.warn('Game stats data must be an array:', data);
        state.gameStats = [];
      }
    },

    setCustomerStats: (state, action) => {
      const data = action.payload;
      if (Array.isArray(data)) {
        state.customerStats = data;
        state.lastUpdated.customerStats = new Date().toISOString();
        state.errorStates.customerStats = null;
      } else {
        console.warn('Customer stats data must be an array:', data);
        state.customerStats = [];
      }
    },

    setCustomers: (state, action) => {
      const data = action.payload;
      if (data && typeof data === 'object') {
        const validatedData = {
          summary: {
            totalCustomers: Number(data.summary?.totalCustomers) || 0,
            totalPlayAmount: Number(data.summary?.totalPlayAmount) || 0,
            totalWinnings: Number(data.summary?.totalWinnings) || 0,
            avgCustomerValue: Number(data.summary?.avgCustomerValue) || 0,
            groupedBy: data.summary?.groupedBy || 'phone',
          },
          customers: Array.isArray(data.customers) ? data.customers : [],
        };
        state.customers = validatedData;
        state.lastUpdated.customers = new Date().toISOString();
        state.errorStates.customers = null;
      } else {
        console.warn('Invalid customers data provided:', data);
      }
    },

    setPlatformStats: (state, action) => {
      const data = action.payload;
      if (Array.isArray(data)) {
        state.platformStats = data;
        state.lastUpdated.platformStats = new Date().toISOString();
        state.errorStates.platformStats = null;
      } else {
        console.warn('Platform stats data must be an array:', data);
        state.platformStats = [];
      }
    },

    setWinLossAnalysis: (state, action) => {
      const data = action.payload;
      if (Array.isArray(data)) {
        state.winLossAnalysis = data;
        state.lastUpdated.winLossAnalysis = new Date().toISOString();
        state.errorStates.winLossAnalysis = null;
      } else {
        console.warn('Win/Loss analysis data must be an array:', data);
        state.winLossAnalysis = [];
      }
    },

    setRecentBets: (state, action) => {
      const data = action.payload;
      if (data && typeof data === 'object') {
        const validatedData = {
          data: Array.isArray(data.data) ? data.data : [],
          pagination: data.pagination && typeof data.pagination === 'object'
            ? { ...DEFAULT_PAGINATION, ...data.pagination }
            : { ...DEFAULT_PAGINATION },
          filters: data.filters && typeof data.filters === 'object' ? data.filters : {}
        };
        state.recentBets = validatedData;
        state.lastUpdated.recentBets = new Date().toISOString();
        state.errorStates.recentBets = null;
      } else {
        console.warn('Invalid recent bets data provided:', data);
      }
    },

    setServiceCodes: (state, action) => {
      const codes = action.payload;
      if (Array.isArray(codes)) {
        const validatedCodes = codes.filter(code =>
          code &&
          typeof code === 'object' &&
          code.hasOwnProperty('value') &&
          code.hasOwnProperty('label')
        );
        state.serviceCodes = validatedCodes;
        state.lastUpdated.serviceCodes = new Date().toISOString();
        state.errorStates.serviceCodes = null;
      } else {
        console.warn('Service codes must be an array:', codes);
        state.serviceCodes = [];
      }
    },

    setLoading: (state, action) => {
      state.loading = Boolean(action.payload);
    },

    setLoadingState: (state, action) => {
      const { section, loading } = action.payload;
      if (state.loadingStates.hasOwnProperty(section)) {
        state.loadingStates[section] = Boolean(loading);
      } else {
        console.warn(`Unknown loading section: ${section}`);
      }
    },

    setError: (state, action) => {
      state.error = action.payload;
    },

    setErrorState: (state, action) => {
      const { section, error } = action.payload;
      if (state.errorStates.hasOwnProperty(section)) {
        state.errorStates[section] = error;
      } else {
        console.warn(`Unknown error section: ${section}`);
      }
    },

    clearError: (state, action) => {
      const section = action.payload;
      if (section && state.errorStates.hasOwnProperty(section)) {
        state.errorStates[section] = null;
      } else {
        state.error = null;
      }
    },

    clearAllErrors: (state) => {
      state.error = null;
      Object.keys(state.errorStates).forEach(key => {
        state.errorStates[key] = null;
      });
    },

    setRecentBetsPagination: (state, action) => {
      const pagination = action.payload;
      if (pagination && typeof pagination === 'object') {
        state.recentBets.pagination = {
          ...state.recentBets.pagination,
          ...pagination,
          limit: Number(pagination.limit) || state.recentBets.pagination.limit,
          offset: Number(pagination.offset) || state.recentBets.pagination.offset,
          total: Number(pagination.total) || state.recentBets.pagination.total,
          currentPage: Number(pagination.currentPage) || state.recentBets.pagination.currentPage,
          totalPages: Number(pagination.totalPages) || state.recentBets.pagination.totalPages,
        };
      }
    },

    clearDashboardData: (state) => {
      return { ...initialState, filters: state.filters };
    },

    updateLeaderboard: (state, action) => {
      const { type, data } = action.payload;
      if (state.leaderboards.hasOwnProperty(type) && Array.isArray(data)) {
        state.leaderboards[type] = data;
        state.lastUpdated.leaderboards = new Date().toISOString();
      } else {
        console.warn(`Invalid leaderboard update: type=${type}, data=`, data);
      }
    },

    mergeChartData: (state, action) => {
      const newData = action.payload;
      if (!Array.isArray(newData)) {
        console.warn('Chart data to merge must be an array:', newData);
        return;
      }
      if (state.chartData.length > 0 && state.chartData[0].data) {
        const existingData = state.chartData[0].data;
        const mergedData = [...existingData, ...newData];
        const sortedData = mergedData
          .sort((a, b) => new Date(a.x) - new Date(b.x))
          .slice(-30);
        state.chartData[0].data = sortedData;
        state.lastUpdated.chart = new Date().toISOString();
      }
    },

    updateStats: (state, action) => {
      const updates = action.payload;
      if (updates && typeof updates === 'object') {
        const validatedUpdates = {};
        Object.keys(updates).forEach(key => {
          if (state.stats.hasOwnProperty(key)) {
            const value = Number(updates[key]);
            if (!isNaN(value) && isFinite(value)) {
              validatedUpdates[key] = value;
            }
          }
        });
        state.stats = { ...state.stats, ...validatedUpdates };
        state.lastUpdated.metrics = new Date().toISOString();
      }
    },

    setBulkLoadingStates: (state, action) => {
      const updates = action.payload;
      if (updates && typeof updates === 'object') {
        Object.keys(updates).forEach(section => {
          if (state.loadingStates.hasOwnProperty(section)) {
            state.loadingStates[section] = Boolean(updates[section]);
          }
        });
      }
    },

    resetDataSection: (state, action) => {
      const section = action.payload;
      const sectionMap = {
        stats: 'stats',
        chartData: 'chartData',
        leaderboards: 'leaderboards',
        revenueBreakdown: 'revenueBreakdown',
        gameStats: 'gameStats',
        customerStats: 'customerStats',
        customers: 'customers',
        platformStats: 'platformStats',
        winLossAnalysis: 'winLossAnalysis',
        recentBets: 'recentBets',
        serviceCodes: 'serviceCodes',
      };
      if (sectionMap[section]) {
        state[sectionMap[section]] = initialState[sectionMap[section]];
        if (state.lastUpdated[section]) {
          state.lastUpdated[section] = null;
        }
        if (state.errorStates[section]) {
          state.errorStates[section] = null;
        }
      }
    },
  },
});

export const {
  updateFilters,
  setQuickFilter,
  setDateRange,
  setServiceCode,
  setPlatform,
  setResultFilter,
  resetFilters,
  setStats,
  setChartData,
  setLeaderboards,
  setRevenueBreakdown,
  setGameStats,
  setCustomerStats,
  setCustomers,
  setPlatformStats,
  setWinLossAnalysis,
  setRecentBets,
  setServiceCodes,
  setLoading,
  setLoadingState,
  setError,
  setErrorState,
  clearError,
  clearAllErrors,
  setRecentBetsPagination,
  setBulkLoadingStates,
  clearDashboardData,
  updateLeaderboard,
  mergeChartData,
  updateStats,
  resetDataSection,
} = dashboardSlice.actions;

export const selectDashboardFilters = (state) => state.dashboard.filters;
export const selectDashboardStats = (state) => state.dashboard.stats;
export const selectDashboardChartData = (state) => state.dashboard.chartData;
export const selectDashboardLeaderboards = (state) => state.dashboard.leaderboards;
export const selectDashboardLoading = (state) => state.dashboard.loading;
export const selectDashboardError = (state) => state.dashboard.error;
export const selectServiceCodes = (state) => state.dashboard.serviceCodes;
export const selectRecentBets = (state) => state.dashboard.recentBets;
export const selectCustomers = (state) => state.dashboard.customers;
export const selectCustomersSummary = (state) => state.dashboard.customers.summary;
export const selectCustomersList = (state) => state.dashboard.customers.customers;
export const selectLoadingStates = (state) => state.dashboard.loadingStates;
export const selectErrorStates = (state) => state.dashboard.errorStates;
export const selectLastUpdated = (state) => state.dashboard.lastUpdated;

export const selectIsAnyLoading = (state) =>
  state.dashboard.loading ||
  Object.values(state.dashboard.loadingStates).some(loading => loading);

export const selectHasAnyError = (state) =>
  state.dashboard.error ||
  Object.values(state.dashboard.errorStates).some(error => error !== null);

export const selectCurrentFiltersAsApiParams = (state) => {
  const filters = state.dashboard.filters;
  const params = {};

  if (filters.serviceCode && filters.serviceCode.trim()) {
    params.service_code = filters.serviceCode.trim();
  }
  if (filters.platform && filters.platform.trim()) {
    params.platform = filters.platform.trim();
  }
  if (filters.resultFilter && filters.resultFilter.trim()) {
    params.result_filter = filters.resultFilter.trim();
  }

  // Ensure dateRange values are valid ISO strings if they exist
  const start = filters.dateRange?.start;
  const end = filters.dateRange?.end;

  if (start && end) {
    try {
      // Attempt to parse to ensure they are valid dates before sending
      params.from = new Date(start).toISOString();
      params.to = new Date(end).toISOString();
    } catch (e) {
      console.warn("Invalid date string in dateRange for API params", start, end);
    }
  } else if (filters.quickFilter && filters.quickFilter.trim()) {
    const period = filters.quickFilter.toLowerCase();
    switch (period) {
      case 'today':
        params.period = 'daily';
        break;
      case 'weekly':
        params.period = 'weekly';
        break;
      case 'monthly':
        params.period = 'monthly';
        break;
      case 'yearly':
        params.period = 'yearly';
        break;
      default:
        params.period = 'all_time';
    }
  }
  return params;
};

export const selectDataFreshness = (state) => {
  const now = new Date();
  const freshness = {};
  Object.entries(state.dashboard.lastUpdated).forEach(([section, timestamp]) => {
    if (timestamp) {
      const lastUpdate = new Date(timestamp);
      const ageInMinutes = (now - lastUpdate) / (1000 * 60);
      freshness[section] = {
        timestamp,
        ageInMinutes: Math.round(ageInMinutes),
        isStale: ageInMinutes > 5,
      };
    } else {
      freshness[section] = {
        timestamp: null,
        ageInMinutes: Infinity,
        isStale: true,
      };
    }
  });
  return freshness;
};

export const selectFiltersValidity = (state) => {
  const filters = state.dashboard.filters;
  const validity = {
    isValid: true,
    errors: [],
  };

  if (filters.dateRange?.start && filters.dateRange?.end) {
    const startDate = new Date(filters.dateRange.start);
    const endDate = new Date(filters.dateRange.end);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      validity.isValid = false;
      validity.errors.push('Invalid date format');
    } else if (startDate > endDate) {
      validity.isValid = false;
      validity.errors.push('Start date cannot be after end date');
    }
  }

  if (filters.quickFilter && !Object.values(QUICK_FILTERS).includes(filters.quickFilter)) {
    validity.isValid = false;
    validity.errors.push('Invalid quick filter selection');
  }
  return validity;
};

export { QUICK_FILTERS };
export default dashboardSlice.reducer;

// import { createSlice } from '@reduxjs/toolkit';

// // Constants for better maintainability
// const QUICK_FILTERS = {
//   TODAY: 'today',
//   WEEKLY: 'weekly',
//   MONTHLY: 'monthly',
//   YEARLY: 'yearly',
//   ALL_TIME: 'all_time'
// };

// const DEFAULT_PAGINATION = {
//   limit: 100,
//   offset: 0,
//   total: 0,
//   hasMore: false,
//   currentPage: 1,
//   totalPages: 1
// };

// const initialState = {
//   filters: {
//     quickFilter: QUICK_FILTERS.TODAY,
//     dateRange: {
//       start: null,
//       end: null,
//     },
//     serviceCode: '',
//     platform: '',
//     resultFilter: '',
//   },
//   stats: {
//     totalPlay: 0,
//     totalWin: 0,
//     customers: 0,
//     revenue: 0,
//     totalGames: 0,
//     totalBets: 0,
//     avgBetAmount: 0,
//     avgWinAmount: 0,
//     winPercentage: 0,
//   },
//   chartData: [],
//   leaderboards: {
//     topWinners: [],
//     topByPoints: [],
//     topPlayers: [],
//     topByGames: [],
//   },
//   revenueBreakdown: [],
//   gameStats: [],
//   customerStats: [],
//   platformStats: [],
//   winLossAnalysis: [],
//   recentBets: {
//     data: [],
//     pagination: { ...DEFAULT_PAGINATION },
//     filters: {}
//   },
//   serviceCodes: [],
//   loading: false,
//   error: null,
//   loadingStates: {
//     metrics: false,
//     chart: false,
//     leaderboards: false,
//     revenueBreakdown: false,
//     gameStats: false,
//     customerStats: false,
//     platformStats: false,
//     winLossAnalysis: false,
//     recentBets: false,
//     serviceCodes: false,
//   },
//   errorStates: {
//     metrics: null,
//     chart: null,
//     leaderboards: null,
//     revenueBreakdown: null,
//     gameStats: null,
//     customerStats: null,
//     platformStats: null,
//     winLossAnalysis: null,
//     recentBets: null,
//     serviceCodes: null,
//   },
//   lastUpdated: {
//     metrics: null,
//     chart: null,
//     leaderboards: null,
//     revenueBreakdown: null,
//     gameStats: null,
//     customerStats: null,
//     platformStats: null,
//     winLossAnalysis: null,
//     recentBets: null,
//     serviceCodes: null,
//   },
// };

// const dashboardSlice = createSlice({
//   name: 'dashboard',
//   initialState,
//   reducers: {
//     updateFilters: (state, action) => {
//       const newFilters = action.payload;
//       if (newFilters.quickFilter && !Object.values(QUICK_FILTERS).includes(newFilters.quickFilter)) {
//         console.warn(`Invalid quick filter: ${newFilters.quickFilter}`);
//         newFilters.quickFilter = QUICK_FILTERS.TODAY;
//       }
//       if (newFilters.dateRange) {
//         const { start, end } = newFilters.dateRange;
//         if (start && end) {
//           const startDate = new Date(start);
//           const endDate = new Date(end);
//           if (isNaN(startDate.getTime()) || isNaN(endDate.getTime()) || startDate > endDate) {
//             console.warn('Invalid date range provided');
//             newFilters.dateRange = { start: null, end: null };
//           }
//         }
//       }
//       state.filters = { ...state.filters, ...newFilters };
//     },

//     setQuickFilter: (state, action) => {
//       const filter = action.payload;
//       // Check if the filter is a valid quick filter option or an empty string (for clearing)
//       if (Object.values(QUICK_FILTERS).includes(filter) || filter === '') {
//         state.filters.quickFilter = filter;
//         // If a *valid, non-empty* quick filter is being set, then clear the date range.
//         // If the quick filter is being cleared (i.e., filter is ''), do not modify the date range.
//         if (filter !== '' && Object.values(QUICK_FILTERS).includes(filter)) {
//           state.filters.dateRange = { start: null, end: null };
//         }
//       } else {
//         console.warn(`Invalid quick filter attempted: ${filter}.`);
//         // Optionally, set a default quick filter if an invalid one is somehow passed
//         // state.filters.quickFilter = QUICK_FILTERS.TODAY;
//         // state.filters.dateRange = { start: null, end: null };
//       }
//     },

//     setDateRange: (state, action) => {
//       const { start, end } = action.payload;

//       const newStart = start ? new Date(start) : null;
//       const newEnd = end ? new Date(end) : null;

//       // Validate date objects
//       if ((start && isNaN(newStart.getTime())) || (end && isNaN(newEnd.getTime()))) {
//           console.warn('Invalid date object or string provided to setDateRange');
//           // Optionally reset to a safe state or do nothing
//           // state.filters.dateRange = { start: null, end: null };
//           return;
//       }

//       // Validate date range logic (start before end)
//       if (newStart && newEnd && newStart > newEnd) {
//           console.warn('Start date cannot be after end date');
//           // Optionally reset to a safe state or do nothing
//           // state.filters.dateRange = { start: state.filters.dateRange.start, end: state.filters.dateRange.start }; // or nullify
//           return;
//       }

//       state.filters.dateRange = {
//         start: newStart ? newStart.toISOString() : null,
//         end: newEnd ? newEnd.toISOString() : null
//       };

//       // Clear quick filter only when a complete custom date range is selected
//       if (newStart && newEnd) {
//         state.filters.quickFilter = '';
//       }
//     },

//     setServiceCode: (state, action) => {
//       const serviceCode = action.payload;
//       state.filters.serviceCode = typeof serviceCode === 'string' ? serviceCode.trim() : '';
//     },

//     setPlatform: (state, action) => {
//       const platform = action.payload;
//       state.filters.platform = typeof platform === 'string' ? platform.trim() : '';
//     },

//     setResultFilter: (state, action) => {
//       const resultFilter = action.payload;
//       state.filters.resultFilter = typeof resultFilter === 'string' ? resultFilter.trim() : '';
//     },

//     resetFilters: (state) => {
//       state.filters = { ...initialState.filters };
//     },

//     setStats: (state, action) => {
//       const stats = action.payload;
//       if (stats && typeof stats === 'object') {
//         state.stats = {
//           totalPlay: Number(stats.totalPlay) || 0,
//           totalWin: Number(stats.totalWin) || 0,
//           customers: Number(stats.customers) || 0,
//           revenue: Number(stats.revenue) || 0,
//           totalGames: Number(stats.totalGames) || 0,
//           totalBets: Number(stats.totalBets) || 0,
//           avgBetAmount: Number(stats.avgBetAmount) || 0,
//           avgWinAmount: Number(stats.avgWinAmount) || 0,
//           winPercentage: Number(stats.winPercentage) || 0,
//         };
//         state.lastUpdated.metrics = new Date().toISOString();
//         state.errorStates.metrics = null;
//       } else {
//         console.warn('Invalid stats data provided:', stats);
//       }
//     },

//     setChartData: (state, action) => {
//       const chartData = action.payload;
//       if (Array.isArray(chartData)) {
//         state.chartData = chartData;
//         state.lastUpdated.chart = new Date().toISOString();
//         state.errorStates.chart = null;
//       } else {
//         console.warn('Chart data must be an array:', chartData);
//         state.chartData = [];
//       }
//     },

//     setLeaderboards: (state, action) => {
//       const leaderboards = action.payload;
//       if (leaderboards && typeof leaderboards === 'object') {
//         const validatedLeaderboards = {};
//         const leaderboardTypes = ['topWinners', 'topByPoints', 'topPlayers', 'topByGames'];
//         leaderboardTypes.forEach(type => {
//           if (Array.isArray(leaderboards[type])) {
//             validatedLeaderboards[type] = leaderboards[type];
//           } else {
//             console.warn(`Invalid leaderboard data for ${type}:`, leaderboards[type]);
//             validatedLeaderboards[type] = [];
//           }
//         });
//         state.leaderboards = { ...state.leaderboards, ...validatedLeaderboards };
//         state.lastUpdated.leaderboards = new Date().toISOString();
//         state.errorStates.leaderboards = null;
//       } else {
//         console.warn('Invalid leaderboards data provided:', leaderboards);
//       }
//     },

//     setRevenueBreakdown: (state, action) => {
//       const data = action.payload;
//       if (Array.isArray(data)) {
//         state.revenueBreakdown = data;
//         state.lastUpdated.revenueBreakdown = new Date().toISOString();
//         state.errorStates.revenueBreakdown = null;
//       } else {
//         console.warn('Revenue breakdown data must be an array:', data);
//         state.revenueBreakdown = [];
//       }
//     },

//     setGameStats: (state, action) => {
//       const data = action.payload;
//       if (Array.isArray(data)) {
//         state.gameStats = data;
//         state.lastUpdated.gameStats = new Date().toISOString();
//         state.errorStates.gameStats = null;
//       } else {
//         console.warn('Game stats data must be an array:', data);
//         state.gameStats = [];
//       }
//     },

//     setCustomerStats: (state, action) => {
//       const data = action.payload;
//       if (Array.isArray(data)) {
//         state.customerStats = data;
//         state.lastUpdated.customerStats = new Date().toISOString();
//         state.errorStates.customerStats = null;
//       } else {
//         console.warn('Customer stats data must be an array:', data);
//         state.customerStats = [];
//       }
//     },

//     setPlatformStats: (state, action) => {
//       const data = action.payload;
//       if (Array.isArray(data)) {
//         state.platformStats = data;
//         state.lastUpdated.platformStats = new Date().toISOString();
//         state.errorStates.platformStats = null;
//       } else {
//         console.warn('Platform stats data must be an array:', data);
//         state.platformStats = [];
//       }
//     },

//     setWinLossAnalysis: (state, action) => {
//       const data = action.payload;
//       if (Array.isArray(data)) {
//         state.winLossAnalysis = data;
//         state.lastUpdated.winLossAnalysis = new Date().toISOString();
//         state.errorStates.winLossAnalysis = null;
//       } else {
//         console.warn('Win/Loss analysis data must be an array:', data);
//         state.winLossAnalysis = [];
//       }
//     },

//     setRecentBets: (state, action) => {
//       const data = action.payload;
//       if (data && typeof data === 'object') {
//         const validatedData = {
//           data: Array.isArray(data.data) ? data.data : [],
//           pagination: data.pagination && typeof data.pagination === 'object'
//             ? { ...DEFAULT_PAGINATION, ...data.pagination }
//             : { ...DEFAULT_PAGINATION },
//           filters: data.filters && typeof data.filters === 'object' ? data.filters : {}
//         };
//         state.recentBets = validatedData;
//         state.lastUpdated.recentBets = new Date().toISOString();
//         state.errorStates.recentBets = null;
//       } else {
//         console.warn('Invalid recent bets data provided:', data);
//       }
//     },

//     setServiceCodes: (state, action) => {
//       const codes = action.payload;
//       if (Array.isArray(codes)) {
//         const validatedCodes = codes.filter(code =>
//           code &&
//           typeof code === 'object' &&
//           code.hasOwnProperty('value') &&
//           code.hasOwnProperty('label')
//         );
//         state.serviceCodes = validatedCodes;
//         state.lastUpdated.serviceCodes = new Date().toISOString();
//         state.errorStates.serviceCodes = null;
//       } else {
//         console.warn('Service codes must be an array:', codes);
//         state.serviceCodes = [];
//       }
//     },

//     setLoading: (state, action) => {
//       state.loading = Boolean(action.payload);
//     },

//     setLoadingState: (state, action) => {
//       const { section, loading } = action.payload;
//       if (state.loadingStates.hasOwnProperty(section)) {
//         state.loadingStates[section] = Boolean(loading);
//       } else {
//         console.warn(`Unknown loading section: ${section}`);
//       }
//     },

//     setError: (state, action) => {
//       state.error = action.payload;
//     },

//     setErrorState: (state, action) => {
//       const { section, error } = action.payload;
//       if (state.errorStates.hasOwnProperty(section)) {
//         state.errorStates[section] = error;
//       } else {
//         console.warn(`Unknown error section: ${section}`);
//       }
//     },

//     clearError: (state, action) => {
//       const section = action.payload;
//       if (section && state.errorStates.hasOwnProperty(section)) {
//         state.errorStates[section] = null;
//       } else {
//         state.error = null;
//       }
//     },

//     clearAllErrors: (state) => {
//       state.error = null;
//       Object.keys(state.errorStates).forEach(key => {
//         state.errorStates[key] = null;
//       });
//     },

//     setRecentBetsPagination: (state, action) => {
//       const pagination = action.payload;
//       if (pagination && typeof pagination === 'object') {
//         state.recentBets.pagination = {
//           ...state.recentBets.pagination,
//           ...pagination,
//           limit: Number(pagination.limit) || state.recentBets.pagination.limit,
//           offset: Number(pagination.offset) || state.recentBets.pagination.offset,
//           total: Number(pagination.total) || state.recentBets.pagination.total,
//           currentPage: Number(pagination.currentPage) || state.recentBets.pagination.currentPage,
//           totalPages: Number(pagination.totalPages) || state.recentBets.pagination.totalPages,
//         };
//       }
//     },

//     clearDashboardData: (state) => {
//       return { ...initialState, filters: state.filters };
//     },

//     updateLeaderboard: (state, action) => {
//       const { type, data } = action.payload;
//       if (state.leaderboards.hasOwnProperty(type) && Array.isArray(data)) {
//         state.leaderboards[type] = data;
//         state.lastUpdated.leaderboards = new Date().toISOString();
//       } else {
//         console.warn(`Invalid leaderboard update: type=${type}, data=`, data);
//       }
//     },

//     mergeChartData: (state, action) => {
//       const newData = action.payload;
//       if (!Array.isArray(newData)) {
//         console.warn('Chart data to merge must be an array:', newData);
//         return;
//       }
//       if (state.chartData.length > 0 && state.chartData[0].data) {
//         const existingData = state.chartData[0].data;
//         const mergedData = [...existingData, ...newData];
//         const sortedData = mergedData
//           .sort((a, b) => new Date(a.x) - new Date(b.x))
//           .slice(-30);
//         state.chartData[0].data = sortedData;
//         state.lastUpdated.chart = new Date().toISOString();
//       }
//     },

//     updateStats: (state, action) => {
//       const updates = action.payload;
//       if (updates && typeof updates === 'object') {
//         const validatedUpdates = {};
//         Object.keys(updates).forEach(key => {
//           if (state.stats.hasOwnProperty(key)) {
//             const value = Number(updates[key]);
//             if (!isNaN(value) && isFinite(value)) {
//               validatedUpdates[key] = value;
//             }
//           }
//         });
//         state.stats = { ...state.stats, ...validatedUpdates };
//         state.lastUpdated.metrics = new Date().toISOString();
//       }
//     },

//     setBulkLoadingStates: (state, action) => {
//       const updates = action.payload;
//       if (updates && typeof updates === 'object') {
//         Object.keys(updates).forEach(section => {
//           if (state.loadingStates.hasOwnProperty(section)) {
//             state.loadingStates[section] = Boolean(updates[section]);
//           }
//         });
//       }
//     },

//     resetDataSection: (state, action) => {
//       const section = action.payload;
//       const sectionMap = {
//         stats: 'stats',
//         chartData: 'chartData',
//         leaderboards: 'leaderboards',
//         revenueBreakdown: 'revenueBreakdown',
//         gameStats: 'gameStats',
//         customerStats: 'customerStats',
//         platformStats: 'platformStats',
//         winLossAnalysis: 'winLossAnalysis',
//         recentBets: 'recentBets',
//         serviceCodes: 'serviceCodes',
//       };
//       if (sectionMap[section]) {
//         state[sectionMap[section]] = initialState[sectionMap[section]];
//         if (state.lastUpdated[section]) {
//           state.lastUpdated[section] = null;
//         }
//         if (state.errorStates[section]) {
//           state.errorStates[section] = null;
//         }
//       }
//     },
//   },
// });

// export const {
//   updateFilters,
//   setQuickFilter,
//   setDateRange,
//   setServiceCode,
//   setPlatform,
//   setResultFilter,
//   resetFilters,
//   setStats,
//   setChartData,
//   setLeaderboards,
//   setRevenueBreakdown,
//   setGameStats,
//   setCustomerStats,
//   setPlatformStats,
//   setWinLossAnalysis,
//   setRecentBets,
//   setServiceCodes,
//   setLoading,
//   setLoadingState,
//   setError,
//   setErrorState,
//   clearError,
//   clearAllErrors,
//   setRecentBetsPagination,
//   setBulkLoadingStates,
//   clearDashboardData,
//   updateLeaderboard,
//   mergeChartData,
//   updateStats,
//   resetDataSection,
// } = dashboardSlice.actions;

// export const selectDashboardFilters = (state) => state.dashboard.filters;
// export const selectDashboardStats = (state) => state.dashboard.stats;
// export const selectDashboardChartData = (state) => state.dashboard.chartData;
// export const selectDashboardLeaderboards = (state) => state.dashboard.leaderboards;
// export const selectDashboardLoading = (state) => state.dashboard.loading;
// export const selectDashboardError = (state) => state.dashboard.error;
// export const selectServiceCodes = (state) => state.dashboard.serviceCodes;
// export const selectRecentBets = (state) => state.dashboard.recentBets;
// export const selectLoadingStates = (state) => state.dashboard.loadingStates;
// export const selectErrorStates = (state) => state.dashboard.errorStates;
// export const selectLastUpdated = (state) => state.dashboard.lastUpdated;

// export const selectIsAnyLoading = (state) =>
//   state.dashboard.loading ||
//   Object.values(state.dashboard.loadingStates).some(loading => loading);

// export const selectHasAnyError = (state) =>
//   state.dashboard.error ||
//   Object.values(state.dashboard.errorStates).some(error => error !== null);

// export const selectCurrentFiltersAsApiParams = (state) => {
//   const filters = state.dashboard.filters;
//   const params = {};

//   if (filters.serviceCode && filters.serviceCode.trim()) {
//     params.service_code = filters.serviceCode.trim();
//   }
//   if (filters.platform && filters.platform.trim()) {
//     params.platform = filters.platform.trim();
//   }
//   if (filters.resultFilter && filters.resultFilter.trim()) {
//     params.result_filter = filters.resultFilter.trim();
//   }

//   // Ensure dateRange values are valid ISO strings if they exist
//   const start = filters.dateRange?.start;
//   const end = filters.dateRange?.end;

//   if (start && end) {
//     try {
//       // Attempt to parse to ensure they are valid dates before sending
//       params.from = new Date(start).toISOString();
//       params.to = new Date(end).toISOString();
//     } catch (e) {
//       console.warn("Invalid date string in dateRange for API params", start, end);
//     }
//   } else if (filters.quickFilter && filters.quickFilter.trim()) {
//     const period = filters.quickFilter.toLowerCase();
//     switch (period) {
//       case 'today':
//         params.period = 'daily';
//         break;
//       case 'weekly':
//         params.period = 'weekly';
//         break;
//       case 'monthly':
//         params.period = 'monthly';
//         break;
//       case 'yearly':
//         params.period = 'yearly';
//         break;
//       default:
//         params.period = 'all_time';
//     }
//   }
//   return params;
// };

// export const selectDataFreshness = (state) => {
//   const now = new Date();
//   const freshness = {};
//   Object.entries(state.dashboard.lastUpdated).forEach(([section, timestamp]) => {
//     if (timestamp) {
//       const lastUpdate = new Date(timestamp);
//       const ageInMinutes = (now - lastUpdate) / (1000 * 60);
//       freshness[section] = {
//         timestamp,
//         ageInMinutes: Math.round(ageInMinutes),
//         isStale: ageInMinutes > 5,
//       };
//     } else {
//       freshness[section] = {
//         timestamp: null,
//         ageInMinutes: Infinity,
//         isStale: true,
//       };
//     }
//   });
//   return freshness;
// };

// export const selectFiltersValidity = (state) => {
//   const filters = state.dashboard.filters;
//   const validity = {
//     isValid: true,
//     errors: [],
//   };

//   if (filters.dateRange?.start && filters.dateRange?.end) {
//     const startDate = new Date(filters.dateRange.start);
//     const endDate = new Date(filters.dateRange.end);
//     if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
//       validity.isValid = false;
//       validity.errors.push('Invalid date format');
//     } else if (startDate > endDate) {
//       validity.isValid = false;
//       validity.errors.push('Start date cannot be after end date');
//     }
//   }

//   if (filters.quickFilter && !Object.values(QUICK_FILTERS).includes(filters.quickFilter)) {
//     validity.isValid = false;
//     validity.errors.push('Invalid quick filter selection');
//   }
//   return validity;
// };

// export { QUICK_FILTERS };
// export default dashboardSlice.reducer;

